/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { MetadataApiService } from './metadata-api.service';
import { DataApiService } from '../data-api/data-api-service';
import { ConfigService } from 'src/app/service/config.service';
import { ParamsBuilder } from '../../utils/params-builder';
import { DateUtils } from '../../utils/date.utils';

/** Flattened PNE result: one row per compound_key. */
export interface Pne {
  surface: string;
  count: number | null;
  compound_key: string;
  type: string;
  id: string;
  alpha_id: string;
}

interface PotentialNamedEntityResponse {
  surface: string;
  compound_keys: string[];
}

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  constructor(
    private metadataApi: MetadataApiService,
    private dataApi: DataApiService,
    private config: ConfigService,
  ) {}

  /** Entity search, enriched with news counts. */
  search(value: string, iparams: any): Observable<any> {
    return this.metadataApi
      .get<any>(`search/entity_search`, { q: value, size: '50' })
      .pipe(
        mergeMap((res) => {
          const portfolio: string[] = res.hits.map(
            (item: any) => `${item.entity.type}:${item.entity.alpha_id}`,
          );

          return this.newsCountItemized(iparams, portfolio).pipe(
            map((countRes) => {
              const countsObject = this.buildCountsObject(countRes);
              const entitiesWithCounts = this.filterAndEnrichEntities(
                res.hits,
                countsObject,
              );
              this.buildParentChildRelations(entitiesWithCounts);
              return { hits: entitiesWithCounts };
            }),
          );
        }),
      );
  }

  /** Entity search without news counts. */
  searchEntity(value: string, size = 20): Observable<any> {
    return this.metadataApi.get(`search/entity_search`, {
      q: value,
      size: String(size),
    });
  }

  /** Combined entity + event + source search, with news counts. */
  searchMeta(
    value: string,
    iparams: any,
    size: number = 50,
    sourceSize: number = 10,
  ): Observable<{
    entity_prefix_search: { hits: any[] };
    event_search: { hits: any[] };
    source_prefix_search: { hits: any[] };
  }> {
    return forkJoin([
      this.metadataApi.get<any>('search', { q: value, size: String(size) }),
      this.metadataApi.get<any>('search/sources_search', {
        q: value,
        size: String(sourceSize),
      }),
    ]).pipe(
      mergeMap(([allSearchResult, sourcesSearchResult]) => {
        const portfolio: string[] =
          allSearchResult.entity_prefix_search.hits.map(
            (item: any) => `${item.entity.type}:${item.entity.alpha_id}`,
          );

        return this.newsCountItemized(iparams, portfolio).pipe(
          map((searchCount) => {
            const countsObject = this.buildCountsObject(searchCount);
            const entitiesWithCounts = this.filterAndEnrichEntities(
              allSearchResult.entity_prefix_search.hits,
              countsObject,
            );
            this.buildParentChildRelations(entitiesWithCounts);

            const urlSources = sourcesSearchResult.hits.filter(
              (item: any) => item.source_url,
            );

            return {
              ...allSearchResult,
              entity_prefix_search: { hits: entitiesWithCounts },
              source_prefix_search: { hits: urlSources },
            };
          }),
        );
      }),
    );
  }

  /** PNE search, enriched and flattened to one Pne per compound_key. */
  searchPne(value: string, iparams: any): Observable<Pne[]> {
    return this.dataApi
      .get<
        PotentialNamedEntityResponse[]
      >('/search/potential_named_entity', this.buildPneSearchParams(value, iparams))
      .pipe(
        mergeMap((rawPnes) => {
          const compoundKeys = rawPnes.flatMap((p) => p.compound_keys);
          if (compoundKeys.length === 0) {
            return of([]);
          }
          return this.newsCountItemized(iparams, compoundKeys).pipe(
            map((countRes) =>
              this.flattenPnes(rawPnes, this.buildCountsObject(countRes)),
            ),
          );
        }),
      );
  }

  // Only emit params accepted by the /search/potential_named_entity OpenAPI
  // spec. from/to are remapped to date_from/date_to by ApiBaseService.getOption.
  private buildPneSearchParams(
    value: string,
    iparams: any,
  ): Record<string, any> {
    const fromto = DateUtils.fromTo(iparams?.time || '7');
    const params: Record<string, any> = {
      q: value,
      from: fromto.dateFrom,
      to: fromto.dateTo,
    };
    if (iparams?.lang && iparams.lang !== 'multi') {
      params['lang'] = iparams.lang;
    }
    return params;
  }

  private newsCountItemized(
    iparams: any,
    portfolio: string[],
  ): Observable<any> {
    const paramsToBeSent = ['period', ...ParamsBuilder.PARAMS_FOR_ALL_REQUESTS];
    const params = iparams
      ? ParamsBuilder.build(
          iparams,
          paramsToBeSent,
          this.config.appConfig.routing.apiproj,
        )
      : {};

    return this.dataApi
      .post<any>('portfolio/news_count_itemized', portfolio, params)
      .pipe(
        catchError(() =>
          of({
            result: portfolio.map((el) => ({
              count: null,
              entity_info: {
                compound_key: el,
                type: el.split(':')[0],
                alpha_id: el.split(':')[1],
              },
            })),
          }),
        ),
      );
  }

  private buildCountsObject(countRes: any): Record<string, number | null> {
    const countsObject: Record<string, number | null> = {};
    if (countRes?.result) {
      countRes.result.forEach((item: any) => {
        countsObject[item.entity_info.compound_key] = item.count;
      });
    }
    return countsObject;
  }

  private filterAndEnrichEntities(
    hits: any[],
    countsObject: Record<string, number | null>,
  ): any[] {
    return hits
      .map((item: any) => ({
        ...item,
        entity: { ...item.entity },
        count: countsObject[`${item.entity.type}:${item.entity.alpha_id}`],
      }))
      .filter(
        (entity: any) =>
          entity.entity.type === 'company' ||
          entity.entity.type === 'organization' ||
          entity.entity.type === 'commodity' ||
          (entity.count && entity.count > 0),
      );
  }

  private flattenPnes(
    rawPnes: PotentialNamedEntityResponse[],
    counts: Record<string, number | null>,
  ): Pne[] {
    const flat: Pne[] = [];
    rawPnes.forEach((pne) => {
      pne.compound_keys.forEach((compoundKey) => {
        const alphaId = compoundKey.split(':')[1] ?? '';
        const [type, id] = alphaId.split('.');
        flat.push({
          surface: pne.surface,
          count: counts[compoundKey] ?? null,
          compound_key: compoundKey,
          type: type ?? '',
          id: id ?? '',
          alpha_id: alphaId,
        });
      });
    });
    return flat;
  }

  private buildParentChildRelations(entities: any[]): void {
    const byCompoundKey = new Map<string, any>();
    console.log('entities', entities);
    entities.forEach((entity) => {
      entity.entity.children = [];
      entity.entity.hasParent = false;
      byCompoundKey.set(entity.entity.compound_key, entity);
    });
    entities.forEach((entity) => {
      if (!entity.entity.parents) return;
      entity.entity.parents.forEach((parent: any) => {
        const parentMatch = byCompoundKey.get(parent.compound_key);
        if (parentMatch && parentMatch !== entity) {
          entity.entity.hasParent = true;
          parentMatch.entity.children.push(entity);
        }
      });
    });
  }
}
